import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Crown, Edit, Trash2, Plus } from "lucide-react";
import Select from "react-select";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";

// Custom Option component with checkbox
const Option = ({ children, isSelected, innerProps }) => {
  return (
    <div
      className="flex items-center px-3 py-2 cursor-pointer hover:bg-blue-50"
      {...innerProps}
    >
      <input
        type="checkbox"
        checked={isSelected}
        className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        onChange={() => {}}
      />
      {children}
    </div>
  );
};

const SubscriptionPlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState("");
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    currency: "USD",
    interval: "month",
    maxCustomers: 100,
    maxGroups: 10,
    maxMessages: 1000,
    features: {},
    isActive: true,
  });

  const [isUnlimited, setIsUnlimited] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchFeatures();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.get("/admin/subscription-plans");
      if (res.data.success) {
        setPlans(res.data.plans);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const res = await api.get("/admin/subscription-features");
      if (res.data.success) {
        setFeatures(res.data.features);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
    }
  };

  const handleFeatureAdd = async () => {
    if (!newFeature.trim()) return;
    try {
      const res = await api.post("/admin/subscription-features", {
        name: newFeature.trim(),
      });
      if (res.data.success) {
        setFeatures([...features, res.data.feature]);
        setNewFeature("");
      }
    } catch (error) {
      console.error("Error adding feature:", error);
      alert(error.response?.data?.message || "Error adding feature");
    }
  };

  const handleFeatureDelete = async (featureId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this feature? This will remove it from all plans."
      )
    ) {
      try {
        await api.delete(`/admin/subscription-features/${featureId}`);
        setFeatures(features.filter((f) => f.id !== featureId));
      } catch (error) {
        console.error("Error deleting feature:", error);
        alert(error.response?.data?.message || "Error deleting feature");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFeatureChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [name]: checked,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        maxCustomers: isUnlimited ? -1 : parseInt(formData.maxCustomers),
        maxGroups: isUnlimited ? -1 : parseInt(formData.maxGroups),
        maxMessages: isUnlimited ? -1 : parseInt(formData.maxMessages),
      };

      if (editingPlan) {
        await api.put(`/admin/subscription-plans/${editingPlan.id}`, payload);
      } else {
        await api.post("/admin/subscription-plans", payload);
      }

      setShowModal(false);
      setEditingPlan(null);
      setIsUnlimited(false);
      setFormData({
        name: "",
        description: "",
        price: "",
        currency: "USD",
        interval: "month",
        maxCustomers: 100,
        maxGroups: 10,
        maxMessages: 1000,
        features: {},
        isActive: true,
      });
      fetchPlans();
    } catch (error) {
      console.error("Error saving plan:", error);
      alert(error.response?.data?.message || "Error saving plan");
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    // Initialize features object with all available features set to false by default
    const initialFeatures = {};
    features.forEach((feature) => {
      initialFeatures[feature.name] = false;
    });

    // Check if plan has unlimited limits
    const unlimited =
      plan.maxCustomers === -1 &&
      plan.maxGroups === -1 &&
      plan.maxMessages === -1;
    setIsUnlimited(unlimited);

    // Then override with the plan's actual features
    setFormData({
      name: plan.name,
      description: plan.description || "",
      price: plan.price.toString(),
      currency: plan.currency,
      interval: plan.interval,
      maxCustomers: plan.maxCustomers === -1 ? 100 : plan.maxCustomers,
      maxGroups: plan.maxGroups === -1 ? 10 : plan.maxGroups,
      maxMessages: plan.maxMessages === -1 ? 1000 : plan.maxMessages,
      features: { ...initialFeatures, ...(plan.features || {}) },
      isActive: plan.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        await api.delete(`/admin/subscription-plans/${planId}`);
        fetchPlans();
      } catch (error) {
        console.error("Error deleting plan:", error);
        alert(error.response?.data?.message || "Error deleting plan");
      }
    }
  };

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={true}
        size="lg"
        text="Loading subscription plans..."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          backTo="/admin/dashboard"
          title="Manage Subscription Plans"
          subtitle="Create and manage subscription plans for your users"
          action={
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  setEditingPlan(null);
                  setIsUnlimited(false);
                  setFormData({
                    name: "",
                    description: "",
                    price: "",
                    currency: "USD",
                    interval: "month",
                    maxCustomers: 100,
                    maxGroups: 10,
                    maxMessages: 1000,
                    features: {},
                    isActive: true,
                  });
                  setShowModal(true);
                }}
                icon={<Plus className="w-5 h-5" />}
              >
                Add New Plan
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowFeatureModal(true)}
                icon={<Plus className="w-5 h-5" />}
              >
                Manage Features
              </Button>
            </div>
          }
        />

        {/* Plans List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Crown className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {plan.currency} {plan.price}/{plan.interval}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(plan)}
                      className="p-2 text-gray-600 hover:text-blue-600 transition"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(plan.id)}
                      className="p-2 text-gray-600 hover:text-red-600 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-medium">Customers:</span>{" "}
                    {plan.maxCustomers === -1 ? "Unlimited" : plan.maxCustomers}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Groups:</span>{" "}
                    {plan.maxGroups === -1 ? "Unlimited" : plan.maxGroups}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Messages:</span>{" "}
                    {plan.maxMessages === -1 ? "Unlimited" : plan.maxMessages}
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-900 mb-2">Features:</h4>
                  <ul className="space-y-1">
                    {Object.entries(plan.features || {}).map(
                      ([feature, included]) => (
                        <li
                          key={feature}
                          className={
                            included
                              ? "text-sm text-gray-900"
                              : "text-sm text-gray-400 line-through"
                          }
                        >
                          {feature}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      plan.isActive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {plan.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingPlan ? "Edit Plan" : "Create New Plan"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency
                      </label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="USD">USD</option>
                        <option value="INR">INR</option>
                        <option value="AED">AED</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Interval
                    </label>
                    <select
                      name="interval"
                      value={formData.interval}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>

                  {/* Unlimited Checkbox */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isUnlimited}
                        onChange={(e) => setIsUnlimited(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        Unlimited Plan
                      </span>
                    </label>
                    <p className="ml-7 mt-1 text-xs text-gray-600">
                      Enable unlimited customers, groups, and messages for this
                      plan
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Customers
                      </label>
                      <input
                        type="number"
                        name="maxCustomers"
                        value={formData.maxCustomers}
                        onChange={handleInputChange}
                        disabled={isUnlimited}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                          isUnlimited
                            ? "bg-gray-100 cursor-not-allowed text-gray-500"
                            : ""
                        }`}
                        required={!isUnlimited}
                      />
                      {isUnlimited && (
                        <p className="mt-1 text-xs text-blue-600 font-medium">
                          Unlimited
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Groups
                      </label>
                      <input
                        type="number"
                        name="maxGroups"
                        value={formData.maxGroups}
                        onChange={handleInputChange}
                        disabled={isUnlimited}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                          isUnlimited
                            ? "bg-gray-100 cursor-not-allowed text-gray-500"
                            : ""
                        }`}
                        required={!isUnlimited}
                      />
                      {isUnlimited && (
                        <p className="mt-1 text-xs text-blue-600 font-medium">
                          Unlimited
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Messages
                      </label>
                      <input
                        type="number"
                        name="maxMessages"
                        value={formData.maxMessages}
                        onChange={handleInputChange}
                        disabled={isUnlimited}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                          isUnlimited
                            ? "bg-gray-100 cursor-not-allowed text-gray-500"
                            : ""
                        }`}
                        required={!isUnlimited}
                      />
                      {isUnlimited && (
                        <p className="mt-1 text-xs text-blue-600 font-medium">
                          Unlimited
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Features
                    </label>
                    <Select
                      isMulti
                      name="features"
                      options={features.map((feature) => ({
                        value: feature.name,
                        label: feature.name,
                      }))}
                      value={Object.entries(formData.features)
                        .filter(([_, included]) => included)
                        .map(([name]) => ({
                          value: name,
                          label: name,
                        }))}
                      onChange={(selectedOptions) => {
                        const newFeatures = {};
                        features.forEach((feature) => {
                          newFeatures[feature.name] =
                            selectedOptions?.some(
                              (option) => option.value === feature.name
                            ) || false;
                        });
                        setFormData((prev) => ({
                          ...prev,
                          features: newFeatures,
                        }));
                      }}
                      className="w-full"
                      classNamePrefix="select"
                      placeholder="Select features..."
                      components={{ Option }}
                      hideSelectedOptions={false}
                      closeMenuOnSelect={false}
                      styles={{
                        container: (base) => ({
                          ...base,
                          position: "relative",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "42px",
                          maxHeight: "80px",
                          borderColor: "#D1D5DB",
                          "&:hover": {
                            borderColor: "#3B82F6",
                          },
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: "100%",
                          padding: "2px 8px",
                          overflow: "auto",
                        }),
                        menu: (base) => ({
                          ...base,
                          width: "100%",
                          position: "absolute",
                          zIndex: 1000,
                          marginTop: "4px",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          borderRadius: "0.5rem",
                          backgroundColor: "white",
                        }),
                        menuList: (base) => ({
                          ...base,
                          maxHeight: "250px",
                          padding: "5px 0",
                          overflowY: "auto",
                          overflowX: "hidden",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#F3F4F6",
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#94A3B8",
                            borderRadius: "3px",
                            "&:hover": {
                              background: "#64748B",
                            },
                          },
                        }),
                        option: (base, { isFocused }) => ({
                          ...base,
                          backgroundColor: isFocused ? "#EFF6FF" : "white",
                          color: "#374151",
                          padding: 0,
                          margin: 0,
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "#EFF6FF",
                          borderRadius: "0.375rem",
                          margin: "1px",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "#2563EB",
                          fontSize: "0.875rem",
                          padding: "2px 6px",
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "#2563EB",
                          padding: "2px",
                          ":hover": {
                            backgroundColor: "#DBEAFE",
                            color: "#1D4ED8",
                          },
                        }),
                      }}
                    />
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Plan is active
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    {editingPlan ? "Update Plan" : "Create Plan"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feature Management Modal */}
      {showFeatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Manage Features
              </h2>

              {/* Add New Feature */}
              <div className="flex items-center gap-2 mb-6">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Enter new feature name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleFeatureAdd}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Add
                </button>
              </div>

              {/* Features List */}
              <div className="space-y-2">
                {features.map((feature) => (
                  <div
                    key={feature.id}
                    className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-gray-700">{feature.name}</span>
                    <button
                      onClick={() => handleFeatureDelete(feature.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowFeatureModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlanManagement;
